import type { Schemas } from "../api/client.ts";
import type { IconName } from "../ui/Icon.tsx";

/**
 * Las catorce dimensiones de SPEC 11, con la pregunta que contesta cada una.
 *
 * El valor de `value` es el del contrato (`BreakdownDimension`), asi que si el
 * servidor anade o quita una dimension esto deja de compilar. Lo de aqui es solo
 * el ORDEN y las palabras; las cifras vienen siempre del servidor.
 */

export type BreakdownDimension = Schemas["BreakdownDimension"];
export type BreakdownRow = Schemas["BreakdownRowDto"];

export type DimensionInfo = {
  value: BreakdownDimension;
  label: string;
  /** Lo que se lee bajo el titulo: para que sirve mirar por aqui. */
  question: string;
};

export type DimensionGroup = {
  title: string;
  icon: IconName;
  items: readonly DimensionInfo[];
};

/**
 * Agrupadas por lo que preguntan, no por el orden del enum. Catorce chips en una
 * sola fila son catorce cosas sin jerarquia; en cuatro bloques se leen.
 */
export const DIMENSION_GROUPS: readonly DimensionGroup[] = [
  {
    title: "Que operaste",
    icon: "layers",
    items: [
      {
        value: "instrument",
        label: "Instrumento",
        question: "En que simbolos ganas y en cuales solo te entretienes",
      },
      {
        value: "strategy",
        label: "Estrategia",
        question: "Que estrategia te da ventaja de verdad",
      },
      { value: "setup", label: "Setup", question: "Que patron paga y cual solo parece bueno" },
      { value: "direction", label: "Direccion", question: "Si compras y vendes igual de bien" },
      {
        value: "marketCondition",
        label: "Condicion de mercado",
        question: "En que mercado funciona lo tuyo",
      },
    ],
  },
  {
    title: "Con que entraste",
    icon: "smile",
    items: [
      {
        value: "emotion",
        label: "Emocion",
        question: "Como te va segun con que emocion entraste",
      },
      {
        value: "emotionPolarity",
        label: "Polaridad emocional",
        question: "Entrar en positivo o en negativo, y lo que cuesta",
      },
      {
        value: "mistake",
        label: "Error",
        question: "Cuanto te cuesta cada error, en R y no en anecdotas",
      },
      {
        value: "confluence",
        label: "Confluencia",
        question: "Que confluencias suman y cuales solo adornan",
      },
    ],
  },
  {
    title: "Cuando",
    icon: "clock",
    items: [
      {
        value: "dayOfWeek",
        label: "Dia de la semana",
        question: "Si hay dias en los que mas te vale no operar",
      },
      { value: "localHour", label: "Hora local", question: "A que horas operas mejor" },
    ],
  },
  {
    title: "Como lo hiciste",
    icon: "check",
    items: [
      {
        value: "exitReason",
        label: "Motivo de salida",
        question: "Como sales: el stop, el objetivo o la mano",
      },
      {
        value: "executionGrade",
        label: "Nota de ejecucion",
        question: "Si ejecutar bien se nota en el resultado",
      },
      {
        value: "adherenceBand",
        label: "Tramo de adherencia",
        question: "Si cumplir el plan entero se paga",
      },
    ],
  },
];

const ALL: readonly DimensionInfo[] = DIMENSION_GROUPS.flatMap((group) => group.items);

/**
 * Las dimensiones DE COLECCION, espejo de `BreakdownDimensions.IsCollection`
 * del servidor: una operacion con dos emociones o dos errores aparece en dos
 * filas, y la suma de la columna supera el total del periodo.
 *
 * Se decide por la DIMENSION y no por si `note` viene o no: desde el ADR 0007
 * `note` tambien avisa de que una suma de pips mezcla instrumentos, y eso pasa
 * en casi cualquier desglose que no sea por instrumento. Leer «hay nota» como
 * «las filas suman de mas» decia que la tabla por estrategia no cuadraba con
 * el total cuando si cuadraba.
 */
const COLLECTION_DIMENSIONS: ReadonlySet<BreakdownDimension> = new Set<BreakdownDimension>([
  "emotion",
  "emotionPolarity",
  "mistake",
  "confluence",
]);

export function isCollectionDimension(dimension: BreakdownDimension): boolean {
  return COLLECTION_DIMENSIONS.has(dimension);
}

/** El pie de la tabla del desglose: si la columna de operaciones cuadra o no. */
export function tableFootText(dimension: BreakdownDimension): string {
  return isCollectionDimension(dimension)
    ? "La suma puede superar el total del periodo, y es lo esperado"
    : "La suma cuadra con el total del periodo";
}

export function dimensionInfo(value: BreakdownDimension): DimensionInfo {
  const found = ALL.find((item) => item.value === value);

  /* No puede faltar: `value` es del tipo del contrato y la lista los cubre
     todos. El respaldo esta para no tener que escribir un aserto. */
  return found ?? { value, label: value, question: "" };
}

/* ========================================================================== */
/* El rango temporal                                                          */
/* ========================================================================== */

export type RangeKey = "all" | "30" | "14" | "7";

export const RANGES: readonly { key: RangeKey; label: string; days: number | null }[] = [
  { key: "all", label: "Todo", days: null },
  { key: "30", label: "30 dias", days: 30 },
  { key: "14", label: "14 dias", days: 14 },
  { key: "7", label: "7 dias", days: 7 },
];

/**
 * El `from` que corresponde al rango, en el dia LOCAL DEL SERVIDOR.
 *
 * SPEC 19.19: la zona horaria la decide el servidor, no el dispositivo. Calcular
 * "hace siete dias" con el reloj del telefono haria que el rango cambiara al
 * viajar, que es justo el bug que esa leccion documenta. `timeZone` sale de
 * `/lookups`.
 */
export function rangeStart(range: RangeKey, timeZone: string): string | undefined {
  const days = RANGES.find((item) => item.key === range)?.days;

  if (days === undefined || days === null) return undefined;

  const today = localToday(timeZone);

  if (today === null) return undefined;

  /* El rango INCLUYE hoy: "7 dias" son hoy y los seis anteriores. */
  const start = new Date(
    Date.UTC(today.year, today.month - 1, today.day) - (days - 1) * 86_400_000,
  );

  const year = pad(start.getUTCFullYear(), 4);
  const month = pad(start.getUTCMonth() + 1, 2);
  const day = pad(start.getUTCDate(), 2);

  return `${year}-${month}-${day}`;
}

/**
 * El dia de HOY en la zona horaria del servidor. Lo exporta el calendario para
 * saber que casilla lleva el contorno y de que mes parte la navegacion: leerlo
 * del reloj del telefono adelantaria o atrasaria el mes al viajar (SPEC 19.19).
 */
export function localToday(timeZone: string): { year: number; month: number; day: number } | null {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const read = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  const year = read("year");
  const month = read("month");
  const day = read("day");

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

  return { year, month, day };
}

function pad(value: number, size: number): string {
  return String(value).padStart(size, "0");
}

/* ========================================================================== */
/* Aritmetica de filas                                                        */
/* ========================================================================== */

/**
 * Junta varias filas en una.
 *
 * SOLO vale cuando la dimension no es de coleccion (`note === null`): ahi cada
 * operacion cae en una fila y en una sola, asi que sumar filas suma operaciones
 * distintas. En una dimension de coleccion la misma operacion esta en varias
 * filas y esta suma contaria su R dos veces (SPEC 19.1).
 */
export function mergeRows(label: string, rows: readonly BreakdownRow[]): BreakdownRow {
  const total = (pick: (row: BreakdownRow) => number): number =>
    rows.reduce((sum, row) => sum + pick(row), 0);

  const trades = total((row) => row.trades);
  const rated = total((row) => row.rated);
  const wins = total((row) => row.wins);
  const losses = total((row) => row.losses);
  const decided = wins + losses;
  const totalR = total((row) => row.totalR);
  const totalPips = total((row) => row.totalPips);

  /* Cuantos instrumentos DISTINTOS hay en la union no se puede saber desde las
     filas: dos tramos con dos instrumentos cada uno pueden ser dos o cuatro.
     Se toma el mayor, que es una cota inferior, y quien pinte la fila junta no
     debe presentar esta cifra como exacta. Hoy nadie la pinta. */
  const instruments = rows.reduce((most, row) => Math.max(most, row.instruments), 0);

  const bests = rows.map((row) => row.bestR).filter(isNumber);
  const worsts = rows.map((row) => row.worstR).filter(isNumber);

  /* La adherencia media se pondera por operaciones: la media de dos medias
     mentiria si un tramo tiene una operacion y el otro treinta. */
  const scored = rows.filter((row) => row.averageAdherence !== null);
  const weight = scored.reduce((sum, row) => sum + row.trades, 0);
  const adherence =
    weight === 0
      ? null
      : scored.reduce((sum, row) => sum + (row.averageAdherence ?? 0) * row.trades, 0) / weight;

  return {
    key: null,
    label,
    trades,
    rated,
    wins,
    losses,
    breakEven: total((row) => row.breakEven),
    winRate: decided === 0 ? null : wins / decided,
    totalR,
    expectancyR: rated === 0 ? null : totalR / rated,
    bestR: bests.length === 0 ? null : Math.max(...bests),
    worstR: worsts.length === 0 ? null : Math.min(...worsts),
    averageAdherence: adherence,
    totalPips,
    instruments,
  };
}

function isNumber(value: number | null): value is number {
  return value !== null;
}
